import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { prazos as api, type PrazoDaFamilia } from '../api/prazos'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { mensagemDeErro } from './mensagemDeErro'
import { formatar } from '../i18n'
import './usuarios.css'

/**
 * O prazo de fabricação **por família** (demanda 160 · decisões D6 e D7 de
 * 02/09).
 *
 * 🔴 **A tela existe porque as duas decisões do cliente não cabem num número
 * só.** No mesmo dia ele decidiu que o prazo fica em **zero, com transbordo** —
 * pela variabilidade da produção — e que **livro informa 8 dias corridos**. Com
 * um campo por marca, zero cumpre a primeira e mata a segunda.
 *
 * ⭐ **Zero não é "vazio": é uma decisão.** Significa *não informado*, e o
 * assistente encaminha a pergunta de prazo a uma pessoa em vez de estimar. A
 * tela precisa dizer isso, senão quem abre conclui que falta cadastrar.
 */
export function Prazos() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const editavel = pode('ParametroGerenciar')

  const [linhas, setLinhas] = useState<PrazoDaFamilia[]>([])
  const [valores, setValores] = useState<Record<string, string>>({})
  const [precisaPublicar, setPrecisaPublicar] = useState(false)
  const [carregando, setCarregando] = useState(true)
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      const lido = await api.listar()

      setLinhas(lido.linhas)
      setPrecisaPublicar(lido.precisaPublicar)
      setValores(
        Object.fromEntries(lido.linhas.map((l) => [l.familia, l.dias === null ? '' : String(l.dias)])),
      )
    } catch {
      setFalhou(true)
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function salvar(e: FormEvent) {
    e.preventDefault()

    setErro(null)
    setRecado(null)
    setEnviando(true)

    try {
      // Só o que mudou — a auditoria responde "quem mudou o quê", e mandar
      // tudo geraria registro de linha que ninguém tocou.
      const mudadas = linhas.filter(
        (l) => (valores[l.familia] ?? '') !== (l.dias === null ? '' : String(l.dias)),
      )

      for (const linha of mudadas) {
        const bruto = (valores[linha.familia] ?? '').trim()
        await api.gravar(linha.familia, bruto === '' ? null : Number(bruto))
      }

      setRecado(
        mudadas.length === 0 ? t('prazos.nadaMudou') : t('prazos.salvo', { total: mudadas.length }),
      )

      await carregar()
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setEnviando(false)
    }
  }

  if (carregando) return <Estado tipo="carregando" />
  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('prazos.titulo')}</h1>
          <p className="usuarios__descricao">{t('prazos.descricao')}</p>
        </div>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {recado && <Aviso tom="sucesso">{recado}</Aviso>}
      {!editavel && <Aviso tom="info">{t('assistentes.somenteLeitura')}</Aviso>}

      {/* 🔴 **Gravar não é publicar** (lição da 123 e da 149). O motor lê a
          configuração publicada; o que está nesta tela é a gravada. Sem este
          aviso, quem edita conclui que a mudança já está valendo — e ela ficou
          sem efeito por até treze dias da última vez. */}
      {precisaPublicar && <Aviso tom="alerta">{t('prazos.precisaPublicar')}</Aviso>}

      <form onSubmit={salvar} noValidate>
        <Cartao titulo={t('prazos.tabela')}>
          <Aviso tom="info">{t('prazos.ajuda')}</Aviso>

          <div className="usuarios__tabela-rolagem" tabIndex={0}>
            <table className="usuarios__tabela">
              <thead>
                <tr>
                  <th scope="col">{t('prazos.familia')}</th>
                  <th scope="col">{t('prazos.dias')}</th>
                  <th scope="col">{t('prazos.valendo')}</th>
                  <th scope="col">{t('prazos.origem')}</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((linha) => (
                  <tr key={linha.familia}>
                    <th scope="row">
                      {linha.familia === 'geral' ? t('prazos.geral') : linha.familia}
                    </th>
                    <td>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={365}
                        value={valores[linha.familia] ?? ''}
                        placeholder={
                          linha.familia === 'geral' ? '0' : t('prazos.usaOGeral')
                        }
                        aria-label={t('prazos.diasDe', { familia: linha.familia })}
                        disabled={!editavel}
                        onChange={(e) =>
                          setValores((v) => ({ ...v, [linha.familia]: e.target.value }))
                        }
                      />
                    </td>
                    <td>
                      {/* ⭐ Zero tem texto próprio: é decisão, não lacuna. */}
                      {linha.efetivo === 0
                        ? t('prazos.semPrazo')
                        : t('prazos.diasCorridos', { total: linha.efetivo })}
                    </td>
                    <td>
                      {linha.usaOGeral ? (
                        <span className="usuarios__etiqueta">{t('prazos.usaOGeral')}</span>
                      ) : (
                        <span>
                          {t('prazos.gravadoPor', {
                            quem: linha.atualizadoPor ?? '—',
                            quando: linha.atualizadoEm ? formatar.data(linha.atualizadoEm) : '—',
                          })}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Cartao>

        {editavel && (
          <Botao type="submit" carregando={enviando}>
            {t('comum.salvar')}
          </Botao>
        )}
      </form>
    </main>
  )
}
